import cn from "classnames";
import Loader from "components/shared/loader";
import ScrollPositionContext, { useScrollPosition } from "contexts/scrollPosition";
import { DebouncedDefaultColumnFilter } from "helpers/reactTable";
import { splitKeyFromProps } from "helpers/reactTableProps";
import { mergeFilters } from "helpers/reqparams";
import debounce from "lodash/debounce";
import moment from "moment";
import { ITEM_ROOT } from "odata-query";
import React, { Component, useCallback, useEffect, useRef } from "react";
import { useExpanded, useFlexLayout, useGroupBy, useResizeColumns, useTable } from "react-table";
import Pagination from "./pagination";

const orderData = (sorted) => {
    if (sorted && Object.keys(sorted).length > 0) {
        const { id, direction } = Object.values(sorted)[0];

        return [id, direction];
    }

    return [null, null];
};

const Table = (props) => {
    const instance = useTable(props, useResizeColumns, useFlexLayout, useGroupBy, useExpanded);
    const scrollContainer = useRef();
    const prevItemTimer = useRef();
    const { setPosition, initialLoad, setInitialLoad, position, setPrevItemId, prevItemId } = useScrollPosition() || {};
    const scrollHandler = useCallback(
        debounce((e) => setPosition?.(e.target.scrollTop), 300),
        [],
    );

    useEffect(() => {
        return () => clearTimeout(prevItemTimer.current);
    }, []);

    useEffect(() => {
        if (initialLoad) {
            setInitialLoad?.(false);
            scrollContainer.current.scrollTop = position;
            clearTimeout(prevItemTimer.current);
            prevItemTimer.current = setTimeout(() => {
                setPrevItemId?.(undefined);
            }, 3000);
        }
        return () => clearTimeout(prevItemTimer.current);
    }, [props.itemsLoadedAt]);

    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = instance;
    const {
        page,
        pages,
        onPageChange,
        onPageSizeChange,
        pageSize,
        pageSizeOptions,
        onSortedChange,
        loading,
        sorted,
        columns,
        data,
        isNotExportable,
        withoutPagination,
        getListExcel,
    } = props;
    if (!columns) {
        return null;
    }

    const paginationProps = {
        page,
        pages,
        pageSize,
        onPageChange,
        onPageSizeChange,
        pageSizeOptions,
        columns,
        data,
        isNotExportable,
        getListExcel,
    };
    const [sortId, sortDirection] = orderData(sorted);
    const resolvedTableProps = getTableProps();

    const iconClass = (id, direction, baseClass) =>
        cn(baseClass, {
            "r-table__sort-icon--active": sortId && sortId === id && sortDirection === direction,
            "r-table__sort-icon--disabled": sortId && (sortId !== id || (sortId === id && sortDirection !== direction)),
        });
    return (
        <>
            {loading && (
                <div className="loader-snippet--disable-loader">
                    <Loader />
                </div>
            )}
            <div {...resolvedTableProps} className="r-table table-sm" onScroll={scrollHandler} ref={scrollContainer}>
                <div className="r-table__thead">
                    {headerGroups.map((headerGroup, i) => {
                        const { key: headerGroupKey, props: headerGroupProps } = splitKeyFromProps(
                            headerGroup.getHeaderGroupProps(),
                        );
                        return (
                            <div key={headerGroupKey ?? i} {...headerGroupProps} className="tr">
                                {headerGroup.headers.map((column, i) => {
                                    const { key: headerKey, props: headerProps } = splitKeyFromProps(
                                        column.getHeaderProps(),
                                    );
                                    return (
                                        <div key={headerKey ?? i} {...headerProps} className="th">
                                            <div
                                                className={`r-table__column-title ${column.sortable ? "r-table__column-title--sortable" : ""}`}
                                                onClick={() => column.sortable && onSortedChange(column)}
                                            >
                                                {column.render("Header")}
                                                {column.sortable && (
                                                    <span className="r-table__sort-icon">
                                                        <i
                                                            className={iconClass(
                                                                column.id,
                                                                "desc",
                                                                "r-table__sort-icon--down",
                                                            )}
                                                        />

                                                        <i
                                                            className={iconClass(
                                                                column.id,
                                                                "asc",
                                                                "r-table__sort-icon--up",
                                                            )}
                                                        />
                                                    </span>
                                                )}
                                            </div>
                                            {column.filterable && (
                                                <div className="r-table__column-filter">{column.render("Filter")}</div>
                                            )}
                                            <div
                                                {...column.getResizerProps()}
                                                className={`r-table__resizer ${column.isResizing ? "isResizing" : ""}`}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
                <div className="r-table__tbody" {...getTableBodyProps()}>
                    {rows.map((row, i) => {
                        prepareRow(row);
                        const { key: rowKey, props: rowProps } = splitKeyFromProps(row.getRowProps());
                        return (
                            <div
                                key={rowKey ?? i}
                                {...rowProps}
                                onClick={() => (props.onRowClick ? props.onRowClick(row) : {})}
                                className={cn("tr", {
                                    [props.rowCname]: !!props.rowCname,
                                    "book-tr__prev-row": row.original?.id && prevItemId === row.original?.id,
                                })}
                            >
                                {row.cells.map((cell, i) => {
                                    const { key: cellKey, props: cellProps } = splitKeyFromProps(cell.getCellProps());
                                    return (
                                        <div key={cellKey ?? i} className="td" {...cellProps}>
                                            {cell.isGrouped ? (
                                                <div {...row.getToggleRowExpandedProps({ title: "" })}>
                                                    <i
                                                        className={cn("r-item__expander", {
                                                            "icon-uEAAE-angle-right-solid": !row.isExpanded,
                                                            "icon-uEA61-angle-down": row.isExpanded,
                                                        })}
                                                    />

                                                    {cell.render("Cell")}
                                                </div>
                                            ) : cell.isAggregated ? (
                                                cell.render("Aggregated")
                                            ) : cell.isPlaceholder ? null : (
                                                cell.render("Cell")
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                    {rows.length === 0 && (
                        <div className="rt-noData">{props.noDataText || "Нет данных для отображения"}</div>
                    )}
                </div>
            </div>
            {!withoutPagination && <Pagination {...paginationProps} />}
        </>
    );
};

export default class SharedReactTable extends Component {
    static contextType = ScrollPositionContext;
    state = {
        page: 0,
        pages: null,
        pageSize: this.props.params.length,
        dataLoaded: false,
        sorted: {},
        filtered: {},
    };

    constructor(props) {
        super(props);
        this.onColumnFilterChange = debounce(this.onColumnFilterChange, 500);
    }

    componentDidMount() {
        const { orderBy, customFilters, start, length } = this.props.params;
        if (orderBy && orderBy.length) {
            const { 0: id, 1: direction } = orderBy[0].split(" ");
            this.setState({ sorted: [{ id, direction }] });
        }
        const filtered = customFilters && Object.keys(customFilters).length ? customFilters : this.props.filtered || {};
        this.context?.setInitialLoad(true);
        this.getList(this.props).then(() =>
            this.setState({ dataLoaded: true, filtered, page: Math.floor(start / length) }),
        );
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        const { start, length } = this.props.params;
        const { params, isLazy } = nextProps;
        const needUpdate = !isLazy || params.start !== start || length !== params.length;
        if (nextProps.shouldUpdate && !nextProps.isLoadingList && needUpdate) {
            this.context?.setInitialLoad(true);
            this.getList(nextProps);
        }
    }

    componentWillUnmount() {
        this.context?.setPrevItemId(undefined);
    }

    getList = ({ getList, params }) => {
        return getList(params);
    };

    changePage = (page) => {
        const start = this.props.params.length * page;
        const { mergeParams } = this.props;
        this.setState({ page });
        mergeParams({ start });
    };

    changePageSize = (pageSize) => {
        const { mergeParams, params } = this.props;
        const newPage = Math.floor(params.start / pageSize);
        const start = newPage * pageSize;
        this.setState({ page: newPage, pageSize });
        mergeParams({ start, length: pageSize });
    };

    changeSort = (column) => {
        const { sorted } = this.state;
        const [sortId, sortDirection] = orderData(sorted);
        const { id, sortKey } = column;
        const { mergeParams, params } = this.props;
        const direction = id === sortId && sortDirection === "asc" ? "desc" : "asc";
        const orderKey = sortKey || id;
        params.orderBy = [[orderKey, direction].join(" ")];
        this.setState({ sorted: [{ id, direction }] });
        mergeFilters(mergeParams, { name: "custom", value: null }, params, this.props.isLazy);
    };

    onColumnFilterChange = () => {
        const { mergeParams, params } = this.props;
        const filteredValue = Object.values(this.state.filtered).reduce(
            (memo, { id, value, filterType, isMulti, ...rest }) => {
                if (!value) {
                    return memo;
                }
                if (
                    ![
                        "neNull",
                        "nullableBoolean",
                        "nullableField",
                        "date",
                        "select",
                        "carAndTrailerSelect",
                        "numeric",
                        "query",
                        "dateTimeRange",
                        "numberRange",
                        "arrayContains",
                        "arrayContainsWithEmpty",
                        "flags",
                        "dateRange",
                        "prepared",
                    ].includes(filterType) &&
                    !isMulti &&
                    value
                ) {
                    value = value.trim();
                }
                switch (filterType) {
                    case "enum": {
                        if (isMulti) {
                            if (!Array.isArray(value) || value.length === 0) {
                                return memo;
                            }
                            memo.filter.and = { ...memo.filter.and, [id]: { any: { [ITEM_ROOT]: { in: value } } } };
                        } else {
                            if (value === "all") {
                                return memo;
                            }
                            memo.filter.and = {
                                ...memo.filter.and,
                                [id]: isNaN(parseInt(value)) ? value : parseInt(value),
                            };
                        }
                        return memo;
                    }
                    case "onlyInsolventEnum": {
                        if (value === "all") {
                            return memo;
                        }
                        if (value && value === "onlyInsolvent") {
                            memo.query.onlyInsolvent = true;
                        } else {
                            memo.filter.and = {
                                ...memo.filter.and,
                                [id]: isNaN(parseInt(value)) ? value : parseInt(value),
                            };
                        }
                        return memo;
                    }
                    case "boolean": {
                        if (value === "all") {
                            return memo;
                        }
                        memo.filter.and = { ...memo.filter.and, [id]: value === "true" };
                        return memo;
                    }
                    case "nullableBoolean": {
                        memo.filter.and = { ...memo.filter.and, [id]: value === "null" ? null : value === "true" };
                        return memo;
                    }
                    case "neNull": {
                        memo.filter.and = { ...memo.filter.and, [id]: { ne: null } };
                        return memo;
                    }
                    case "nullableField": {
                        if (value === "all") {
                            return memo;
                        }
                        memo.filter.and = { ...memo.filter.and, [id]: value === "null" ? null : { ne: null } };
                        return memo;
                    }
                    case "numberRange": {
                        if (!value.start && !value.end) {
                            return memo;
                        }
                        memo.filter.and = {
                            ...memo.filter.and,
                            [id]: {
                                ...(value.start ? { ge: value.start } : {}),
                                ...(value.end ? { le: value.end } : {}),
                            },
                        };
                        return memo;
                    }
                    case "dateRange": {
                        if (!value.start && !value.end) {
                            return memo;
                        }
                        memo.filter.and = {
                            ...memo.filter.and,
                            [id]: {
                                ...(value.start
                                    ? { ge: { type: "raw", value: moment(value.start).format("YYYY-MM-DD") } }
                                    : {}),
                                ...(value.end
                                    ? { le: { type: "raw", value: moment(value.end).format("YYYY-MM-DD") } }
                                    : {}),
                            },
                        };
                        return memo;
                    }
                    case "dateTimeRange": {
                        if (!value.start && !value.end) {
                            return memo;
                        }
                        memo.filter.and = {
                            ...memo.filter.and,
                            [id]: {
                                ...(value.start ? { ge: moment(value.start).toDate() } : {}),
                                ...(value.end ? { le: moment(value.end).toDate() } : {}),
                            },
                        };
                        return memo;
                    }
                    case "date": {
                        if (value.value) {
                            const dateValue = moment(value.value).toDate();
                            const filterValue =
                                value.key === "eq"
                                    ? {
                                          ge: moment(value.value).startOf("minute").toDate(),
                                          le: moment(value.value).endOf("minute").toDate(),
                                      }
                                    : { [value.key]: dateValue };
                            memo.filter.and = { ...memo.filter.and, [id]: filterValue };
                        }
                        return memo;
                    }
                    case "numeric": {
                        if (value.value) {
                            const numValue = parseInt(value.value);
                            if (isNaN(numValue)) {
                                return memo;
                            }
                            const filterValue = { [value.key]: numValue };
                            memo.filter.and = { ...memo.filter.and, [id]: filterValue };
                        }
                        return memo;
                    }
                    case "int": {
                        const idVal = parseInt(value);
                        if (!isNaN(idVal)) {
                            memo.filter.and = {
                                ...memo.filter.and,
                                [`cast(${id}, Model.String)`]: { contains: value },
                            };
                        }
                        return memo;
                    }
                    case "carAndTrailerSelect": {
                        const idVal = parseInt(value);
                        if (!isNaN(idVal)) {
                            memo.filter.and = {
                                ...memo.filter.and,
                                [id]: idVal,
                                "activeTrackerPeriod/type": rest.option.type,
                            };
                        }
                        return memo;
                    }
                    case "arrayContainsWithEmpty": {
                        if (value) {
                            memo.filter.and = {
                                ...memo.filter.and,
                                or: [{ not: { [id]: { any: [] } } }, { [id]: { any: { [ITEM_ROOT]: value } } }],
                            };
                        }
                        return memo;
                    }
                    case "arrayContains": {
                        if (Array.isArray(value) ? value.length > 0 : value) {
                            memo.filter.and = { ...memo.filter.and, [id]: { any: { [ITEM_ROOT]: value } } };
                        }
                        return memo;
                    }
                    case "flags": {
                        if (value && value.length > 0) {
                            memo.filter.and = { ...memo.filter.and, and: value.map((val) => ({ [id]: { has: val } })) };
                        }
                        return memo;
                    }
                    case "select": {
                        if (isMulti) {
                            if (!Array.isArray(value) || value.length === 0) {
                                return memo;
                            }
                            const idVal = value.map((v) => parseInt(v));
                            if (idVal.some(isNaN)) {
                                return memo;
                            }
                            memo.filter.and = { ...memo.filter.and, [id]: { any: { id: { in: idVal } } } };
                            return memo;
                        } else {
                            const idVal = parseInt(value);
                            if (!isNaN(idVal)) {
                                memo.filter.and = { ...memo.filter.and, [id]: { eq: idVal } };
                            }
                            return memo;
                        }
                    }
                    case "inArray": {
                        if (isMulti) {
                            if (!Array.isArray(value) || value.length === 0) {
                                return memo;
                            }
                            const idVal = value.map((v) => parseInt(v));
                            if (idVal.some(isNaN)) {
                                return memo;
                            }
                            memo.filter.and = { ...memo.filter.and, [id]: { in: idVal } };
                        }
                        return memo;
                    }
                    case "fullName":
                        memo.filter.and = {
                            ...memo.filter.and,
                            or: [
                                {
                                    "tolower(lastName)": {
                                        contains: {
                                            type: "raw",
                                            value: `'${encodeURIComponent(value.toLowerCase())}'`,
                                        },
                                    },
                                },
                                {
                                    "tolower(firstName)": {
                                        contains: {
                                            type: "raw",
                                            value: `'${encodeURIComponent(value.toLowerCase())}'`,
                                        },
                                    },
                                },
                                {
                                    "tolower(patronymic)": {
                                        contains: {
                                            type: "raw",
                                            value: `'${encodeURIComponent(value.toLowerCase())}'`,
                                        },
                                    },
                                },
                            ],
                        };
                        return memo;
                    case "query":
                        if (value === "all") {
                            return memo;
                        }
                        memo.query[id] = value;
                        return memo;
                    case "prepared":
                        memo.filter.and = { ...memo.filter.and, ...(rest.preparedFilter || {}) };
                        return memo;
                    default:
                        memo.filter.and = {
                            ...memo.filter.and,
                            [`tolower(${id})`]: {
                                contains: { type: "raw", value: `'${encodeURIComponent(value.toLowerCase())}'` },
                            },
                        };
                        return memo;
                }
            },
            { filter: { and: {} }, query: {} },
        );
        params.queryFilter = filteredValue.query;
        params.preparedFilter = filteredValue.filter;
        params.customFilters = this.state.filtered;
        mergeFilters(mergeParams, { name: "custom", value: null }, params, this.props.isLazy);
    };

    render() {
        const { dataLoaded } = this.state;
        if (!dataLoaded) {
            return <Loader prefix="absolute-center" />;
        }
        const {
            isLoadingList,
            columns,
            tableProps,
            wrapCname,
            items,
            isNotExportable,
            withoutPagination,
            source,
            params,
        } = this.props;

        const props = {
            ...this.state,
            withoutPagination,
            isNotExportable,
            pageSizeOptions: this.props.pageSizeOptions || [30, 50, 100],
            columns,
            data: items,
            pages: Math.ceil(this.props.count / this.props.params.length),
            loading: isLoadingList,
            onPageChange: this.changePage,
            onPageSizeChange: this.changePageSize,
            onSortedChange: this.changeSort,
            defaultColumn: {
                sortable: true,
                Filter: DebouncedDefaultColumnFilter,
            },
            onFilteredChange: (payload) => {
                const newFilters = this.state.filtered;

                if (Array.isArray(payload)) {
                    payload.forEach(({ id, ...restPaylod }) => {
                        newFilters[id] = { id, ...restPaylod };
                    });
                } else {
                    const { id, ...restPaylod } = payload;

                    newFilters[id] = { id, ...restPaylod };
                }

                this.setState({ filtered: newFilters });

                this.onColumnFilterChange();
            },
            getListExcel: source?.getListExcel ? () => source?.getListExcel(params) : undefined,
            ...tableProps,
        };
        const wrapCnames = cn("list__body-wrap", {
            "container-fluid pb-3": !wrapCname,
            [wrapCname]: !!wrapCname,
        });
        return (
            <div className={wrapCnames}>
                <div className="list__body d-flex flex-column">
                    <Table columns={columns} data={items} {...props} />
                </div>
            </div>
        );
    }
}
